import classNames from 'classnames';
import React, { useState } from 'react';
import { Container } from 'src/components/layouts/Container/Container';
import FilterRow from 'src/components/layouts/FilterRow/FilterRow';
import CountryGroupCard from 'src/components/ui/CountryGroupCard/CountryGroupCard';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import { useTranslation } from 'react-i18next';
import TabBar, { TabProps } from 'src/components/layouts/TabBar/TabBar';
import CountriesTable from 'src/components/layouts/CountriesTable/CountriesTable';
import useFilters from 'src/services/filters';
import { useSearchParams, Outlet } from 'react-router-dom';
import styles from './styles/CountriesAndGroups.module.scss';
import { ButtonToneProps } from '../components/ui/Button/Button';
import { useAllCountryGroupsQuery } from '../services/countries';
import Overlay from '../components/ui/Overlay/Overlay';
import OverlayHead from '../components/ui/Overlay/OverlayHead';
import { CountryGroupOverlay } from './CountryGroupOverlay';

enum TabKeys {
  COUNTRIES = 'countries',
  COUNTRY_GROUPS = 'country_groups',
}

const tabLabels: Record<TabKeys, string> = {
  [TabKeys.COUNTRIES]: 'general.countries',
  [TabKeys.COUNTRY_GROUPS]: 'general.country_groups',
};

function CountriesAndGroups() {
  const { t } = useTranslation();
  const countryFilterInstance = useFilters('res.country');
  const countryGroupFilterInstance = useFilters('res.country.group');
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabKeys) || TabKeys.COUNTRIES;
  const [activeTab, setActiveTab] = useState<TabKeys>(initialTab);

  const [createNewCountryGroup, setCreateNewCountryGroup] = useState<boolean>(false);

  // const { findAccessRights } = useNavigationItems();
  // const accessRights = findAccessRights('carrier-analytics');
  const accessRights = { read_write: true };

  const handleTabChange = (tabKey: TabKeys) => {
    setActiveTab(tabKey);
    setSearchParams({ tab: tabKey }); // Update the search parameters
  };

  const tabs: TabProps[] = [
    {
      label: t(tabLabels[TabKeys.COUNTRIES]),
      key: TabKeys.COUNTRIES,
      onClick: () => handleTabChange(TabKeys.COUNTRIES),
    },
    {
      label: t(tabLabels[TabKeys.COUNTRY_GROUPS]),
      key: TabKeys.COUNTRY_GROUPS,
      onClick: () => handleTabChange(TabKeys.COUNTRY_GROUPS),
    },
  ];

  const activeIndex = tabs.findIndex(tab => tab.key === activeTab);
  const { data: groups } = useAllCountryGroupsQuery({ domain: countryGroupFilterInstance.domain });

  const actions =
    activeTab === TabKeys.COUNTRY_GROUPS && accessRights?.read_write
      ? [
          {
            title: t('general.add_new_group'),
            tone: ButtonToneProps.PRIMARY_BLUE,
            onClick: () => setCreateNewCountryGroup(true),
          },
        ]
      : [];

  return (
    <Container className={classNames(styles.root)}>
      <HeadlineWithButtons headline={t('navbar.countries_and_groups')} buttons={actions} />
      <TabBar className="mt-40 mb-24" tabs={tabs} defaultIndex={activeIndex} />
      {activeTab === TabKeys.COUNTRIES && (
        <CountriesTable filters={countryFilterInstance} allowEdition={accessRights?.read_write} />
      )}
      {activeTab === TabKeys.COUNTRY_GROUPS && (
        <>
          <div className="mb-24">
            <FilterRow
              withSaveFilters={false}
              filterInstance={countryGroupFilterInstance}
              primaryKeys={[{ key: 'custom', operator: '=' }]}
              singleParam
            />
          </div>
          <div className="">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4  gap-x-20 gap-y-20">
              {groups?.items &&
                groups?.items.map(group => (
                  <CountryGroupCard {...(group as any)} key={group.id} allowEdition={accessRights?.read_write} />
                ))}
            </div>
          </div>
        </>
      )}
      <Outlet />
      {createNewCountryGroup && (
        <Overlay>
          <OverlayHead title={t('general.add_new_group')} onCloseOverlay={() => setCreateNewCountryGroup(false)} />
          <CountryGroupOverlay onCustomClose={() => setCreateNewCountryGroup(false)} />
        </Overlay>
      )}
    </Container>
  );
}

export default CountriesAndGroups;
